import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ChartCard } from '@components/shared/CampaignAnalytics/ChartCard/ChartCard';
import { EditableChartProps } from './chartProps';
import {
  BuildChartFunction,
  buildAgeChart,
  buildEducationChart,
  buildGenderChart,
  buildHomeownershipChart,
  buildIncomeChart,
  buildMaritalStatusChart,
  buildChildrenPresenceChart,
  buildPetsChart,
  getChartColor,
} from '@configurations/campaignAnalytics/demographicProfileChart';
import { NameValuePairWithSortIndex } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { Box } from '@mui/material';

type DemographicProfileProps = EditableChartProps;

type DemographicProfileChartBuilder = {
  stats: NameValuePairWithSortIndex[];
  buildChart: BuildChartFunction;
};

export const DemographicProfile = (props: DemographicProfileProps) => {
  const { report, ...chartCardProps } = props;

  if (!report) return null;

  const charts: DemographicProfileChartBuilder[] = [
    {
      stats: report.demographicProfile.data.visited.statsByGender,
      buildChart: buildGenderChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByMaritalStatus,
      buildChart: buildMaritalStatusChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByChildrenPresence,
      buildChart: buildChildrenPresenceChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByHomeownership,
      buildChart: buildHomeownershipChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByEducation,
      buildChart: buildEducationChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByPets,
      buildChart: buildPetsChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByAge,
      buildChart: buildAgeChart,
    },
    {
      stats: report.demographicProfile.data.visited.statsByIncome,
      buildChart: buildIncomeChart,
    },
  ]
    .filter(
      chart =>
        chart.stats.length > 0 && !chart.stats.every(item => item.value === 0)
    )
    .map((chart, index) => {
      const color = getChartColor(index);
      return {
        ...chart,
        buildChart: chart.buildChart(color),
      };
    });

  return (
    <ChartCard
      title="Demographic Profile (Saw the Ad and Visited)"
      description={`${props.campaignName} (${props.campaignId})`}
      report={report.demographicProfile}
      {...chartCardProps}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(auto-fit, minmax(200px, 1fr))',
            sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            md: 'repeat(auto-fit, minmax(500px, 1fr))',
          },
          gap: '1rem',
        }}
      >
        {charts.map((chart, index) => (
          <Box key={index}>
            <HighchartsReact
              highcharts={Highcharts}
              options={chart.buildChart(chart.stats)}
            />
          </Box>
        ))}
      </Box>
    </ChartCard>
  );
};
